import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, startOfWeek, startOfMonth, format } from "date-fns";

export interface Order {
  id: string;
  ordernumber: string;
  producttitle: string | null;
  varianttitle: string | null;
  sku: string | null;
  artikel_code: string | null;
  quantityordered: number | null;
  customfieldcolor: string | null;
  voornaam_klant: string | null;
  achternaam_klant: string | null;
  addressshippingcity: string | null;
  createdat: string | null;
  status: string | null;
  image_url: string | null;
  ean: string | null;
}

export interface OrdersFilters {
  search: string;
  statusFilter: string;
  dateFilter: string;
}

export interface OrdersStats {
  totalOrders: number;
  todayOrders: number;
  awaitingShipment: number;
  totalLines: number;
}

const STATUS_OPTIONS = [
  { value: "processing_awaiting_shipment", label: "Wachtend op verzending" },
  { value: "processing_awaiting_payment", label: "Wachtend op betaling" },
  { value: "processing_ready_for_pickup", label: "Klaar voor ophalen" },
  { value: "processing_awaiting_pickup", label: "Wachtend op ophalen" },
  { value: "all", label: "Alle statussen" },
];

export const getStatusLabel = (status: string): string => {
  const found = STATUS_OPTIONS.find(s => s.value === status);
  return found?.label || status;
};

export const useOrders = (filters: OrdersFilters) => {
  return useQuery({
    queryKey: ["orders", filters],
    queryFn: async () => {
      let query = supabase
        .from("orders_live_ls")
        .select("id, ordernumber, producttitle, varianttitle, sku, artikel_code, quantityordered, customfieldcolor, voornaam_klant, achternaam_klant, addressshippingcity, createdat, status, image_url, ean")
        .order("createdat", { ascending: false })
        .limit(1000);

      // Status filter
      if (filters.statusFilter && filters.statusFilter !== "all") {
        query = query.eq("status", filters.statusFilter);
      }

      // Date filter
      if (filters.dateFilter && filters.dateFilter !== "all") {
        const now = new Date();
        let startDate: Date;
        
        switch (filters.dateFilter) {
          case "today":
            startDate = startOfDay(now);
            break;
          case "week":
            startDate = startOfWeek(now, { weekStartsOn: 1 });
            break;
          case "month":
            startDate = startOfMonth(now);
            break;
          default:
            startDate = new Date(0);
        }
        
        query = query.gte("createdat", startDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error("[useOrders] Error:", error);
        throw error;
      }

      // Client-side search filtering
      let filteredData = data as Order[];
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredData = filteredData.filter(order => 
          order.ordernumber?.toLowerCase().includes(searchLower) ||
          order.producttitle?.toLowerCase().includes(searchLower) ||
          order.sku?.toLowerCase().includes(searchLower) ||
          order.artikel_code?.toLowerCase().includes(searchLower) ||
          order.voornaam_klant?.toLowerCase().includes(searchLower) ||
          order.achternaam_klant?.toLowerCase().includes(searchLower) ||
          order.addressshippingcity?.toLowerCase().includes(searchLower)
        );
      }

      // Deduplicate lines with same ordernumber + sku + varianttitle + customfieldcolor
      // Two sources write to orders_live_ls: our edge function (full data, artikel_code=sku)
      // and an external app (partial data, artikel_code!=sku). We keep the richest record.
      const dedupeMap = new Map<string, Order>();
      for (const order of filteredData) {
        const key = `${order.ordernumber}||${order.sku}||${order.varianttitle}||${order.customfieldcolor}`;
        const existing = dedupeMap.get(key);
        if (existing) {
          existing.quantityordered = Math.max(existing.quantityordered || 0, order.quantityordered || 0);
          if (!existing.voornaam_klant && order.voornaam_klant) existing.voornaam_klant = order.voornaam_klant;
          if (!existing.achternaam_klant && order.achternaam_klant) existing.achternaam_klant = order.achternaam_klant;
          if (!existing.addressshippingcity && order.addressshippingcity) existing.addressshippingcity = order.addressshippingcity;
          if (!existing.image_url && order.image_url) existing.image_url = order.image_url;
        } else {
          dedupeMap.set(key, { ...order });
        }
      }
      const dedupedData = Array.from(dedupeMap.values());

      console.log(`[useOrders] Fetched ${filteredData.length} lines, deduped to ${dedupedData.length}`);
      return dedupedData;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useOrdersStats = () => {
  return useQuery({
    queryKey: ["orders-stats"],
    queryFn: async () => {
      const todayStart = startOfDay(new Date()).toISOString();

      // Get awaiting shipment count
      const { count: awaitingShipmentCount, error: error1 } = await supabase
        .from("orders_live_ls")
        .select("ordernumber", { count: "exact", head: true })
        .eq("status", "processing_awaiting_shipment");

      // Get total unique orders with processing status
      const { data: processingOrders, error: error2 } = await supabase
        .from("orders_live_ls")
        .select("ordernumber")
        .in("status", ["processing_awaiting_shipment", "processing_awaiting_payment", "processing_ready_for_pickup", "processing_awaiting_pickup"]);

      // Get today's orders
      const { data: todayOrdersData, error: error3 } = await supabase
        .from("orders_live_ls")
        .select("ordernumber")
        .eq("status", "processing_awaiting_shipment")
        .gte("createdat", todayStart);

      if (error1 || error2 || error3) {
        console.error("[useOrdersStats] Error:", error1 || error2 || error3);
        throw error1 || error2 || error3;
      }

      // Count unique order numbers
      const uniqueProcessingOrders = new Set(processingOrders?.map(o => o.ordernumber)).size;
      const uniqueTodayOrders = new Set(todayOrdersData?.map(o => o.ordernumber)).size;
      const uniqueAwaitingOrders = awaitingShipmentCount || 0;

      const stats: OrdersStats = {
        totalOrders: uniqueProcessingOrders,
        todayOrders: uniqueTodayOrders,
        awaitingShipment: uniqueAwaitingOrders,
        totalLines: processingOrders?.length || 0,
      };

      console.log("[useOrdersStats] Stats:", stats);
      return stats;
    },
    staleTime: 30 * 1000,
  });
};

export { STATUS_OPTIONS };
